import type { ReactNode } from 'react'
import cx from 'classnames'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { match } from '@/utils/pattern-match-util'
import styles from './PublishPanel.module.css'

/**
 * The outcome of saving and checking the legend before publishing.
 */
export type PublishCheck = 'Pending' | 'Ready' | 'Invalid' | 'Failed'

export interface PublishPanelProps {
  version: number
  check: PublishCheck
  isPublishPending: boolean
  onClose: () => unknown
  onPublish: () => unknown
  /**
   * The version notes field.
   */
  children?: ReactNode
  testId?: string
}

/**
 * Version notes and a final check before publishing.
 */
export function PublishPanel({
  version,
  check,
  isPublishPending,
  onClose,
  onPublish,
  children,
  testId,
}: PublishPanelProps) {
  return (
    <div className={styles.root} data-test={testId}>
      <div className={styles.header}>
        <span className={styles.title}>Publish</span>
        <span className={styles.subtitle}>
          Clients will see V{version} once it's published.
        </span>
      </div>
      <div className={styles.body}>{children}</div>
      <div className={styles.footer}>
        <div
          className={cx(
            styles.status,
            match(check, {
              Pending: undefined,
              Ready: styles.ready,
              Invalid: styles.invalid,
              Failed: styles.invalid,
            }),
          )}
        >
          {check === 'Pending' ? (
            <Spinner size={14} />
          ) : (
            <Icon
              name={check === 'Ready' ? 'status-check' : 'status-alert'}
              size={15}
            />
          )}
          {match(check, {
            Pending: 'Checking the legend…',
            Ready: 'Ready for publishing.',
            Invalid: 'There are unresolved issues',
            Failed: 'Something went wrong',
          })}
        </div>
        <Button
          label="Close"
          size="small"
          isDisabled={isPublishPending}
          onPress={onClose}
        />
        <Button
          label="Publish"
          size="small"
          variant="primary"
          isPending={isPublishPending}
          isDisabled={check !== 'Ready'}
          onPress={onPublish}
          testId={testId && `${testId}-confirm`}
        />
      </div>
    </div>
  )
}
